import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { TrialTypesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsTrialSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as TrialTypeActions from "../../../actions/trialType";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { TrialLookupsLayout } from "../../index";

import "./TrialTypesPage.scss";

interface TrialTypesPageProps {
    trialTypes: Dtos.TrialType[];
    formFilter: IFormFilter;
    loadingTrialTypes: boolean;
    loadTrialTypesSuccess: boolean;
    loadTrialTypesFailure: boolean;
    loadTrialTypesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearTrialTypes: TrialTypeActions.ITrialTypeClearActionCreator;
    loadTrialTypes: TrialTypeActions.ITrialTypeLoadActionCreator;
    filter: TrialTypeActions.ITrialTypeFilterActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const TrialTypesPage: FC<TrialTypesPageProps> = ({
    trialTypes,
    formFilter,
    loadingTrialTypes,
    loadTrialTypesSuccess,
    loadTrialTypesFailure,
    loadTrialTypesErrorCode,
    permissions,
    pageSize,
    clearTrialTypes,
    loadTrialTypes,
    filter,
    setPageSize,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showTrialTypes = !loadingTrialTypes && loadTrialTypesSuccess;


    // Load trial types
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsTrialSubMenu(permissions));
            setBreadcrumbs(TrialTypesBreadcrumbs());

            loadTrialTypes();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsTrialSubMenu,
        permissions,
        setBreadcrumbs,
        TrialTypesBreadcrumbs,
        loadTrialTypes,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialTypesFailure) {
            navigate(`/error/${loadTrialTypesErrorCode}`);
        }
    }, [loadTrialTypesFailure, loadTrialTypesErrorCode, navigate]);

    // Cleanup effect to clear the trial types on component unmount
    useEffect(() => {
        return () => {
            clearTrialTypes();
        };
    }, [clearTrialTypes]);

    const createTable = useMemo(() => {
        if (!trialTypes || trialTypes.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>There are currently no Trial Types to view.</span>
                    </div>
                </div>
            );
        }

        const showPagination = trialTypes.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={trialTypes}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : trialTypes.length
                    }
                    pageSize={showPagination ? pageSize : trialTypes.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no Trial Types to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string = `/settings/trial/trialtypes/${rowInfo.original.id}`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [trialTypes, PAGE_SIZE_OPTIONS, pageSize, navigate]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Trial Types</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.TrialTypesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={"/settings/trial/trialtypes/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showTrialTypes ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showTrialTypes ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [formFilter, filter, permissions, showTrialTypes, createTable]);

    return (
        <TrialLookupsLayout
            loading={loadingTrialTypes}
            permission={Dtos.Permission.TrialTypesView}
        >
            {renderContent}
        </TrialLookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<TrialTypesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialTypes: state.trialTypes.formFilteredData!,
        formFilter: state.trialTypes.formFilter,
        loadingTrialTypes:
            state.trialTypes.loadState &&
            state.trialTypes.loadState.status == RequestState.Pending,
        loadTrialTypesSuccess:
            state.trialTypes.loadState &&
            state.trialTypes.loadState.status == RequestState.Success,
        loadTrialTypesFailure:
            state.trialTypes.loadState &&
            state.trialTypes.loadState.status == RequestState.Failure,
        loadTrialTypesErrorCode: state.trialTypes.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialTypesPageProps> => {
    return {
        loadTrialTypes: bindActionCreators(
            TrialTypeActions.LoadTrialType,
            dispatch
        ),
        clearTrialTypes: bindActionCreators(TrialTypeActions.Clear, dispatch),
        filter: bindActionCreators(TrialTypeActions.Filter, dispatch),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrialTypesPage);
